import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import { Products } from 'src/app/models/Product.model';
import { ProductsService } from 'src/app/services/products.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * ProductDetailModalComponent — versione modal-embedded del Product
 * Detail. Stesso pattern visivo Diakonix di `<app-product-detail>`
 * (B.3 v2.7.0) ma con API @Input-driven invece di route-driven.
 *
 * Usato come sub-modal da:
 *   • storico-ordini-dettagli / product-details-storico (H.5 v2.9.0)
 *   • borderò-dettagli (C.2 v2.7.0)
 *   • preparazione-ordini-detaggli + confirm-products
 *
 * Apertura via `closeServ.prodDetOn()` (set isProdDetOn=true) e
 * `closeServ.onStoricoProd()` (set isStoricoProd=true). Chiusura via
 * `close()` interno che chiama entrambi i toggle off.
 *
 * Phase 1.5 / v2.9.0 — restyle pattern Diakonix (post-test polish):
 *   • dropped ~60 righe di codice commentato dentro a ngOnInit
 *     (vecchio block patchValue + console.log debug),
 *   • dropped ~6 console.log/debug ("product:", "Position is:",
 *     "Product is", "This is the ID:"),
 *   • dropped dead state: `id` (mai usato in template, `pr_code` è
 *     l'unica fonte di verità), `route`, `pr_desc`, `pr_lane`,
 *     `pr_qty`, `productDetails`, `clickedProduct`, `isZoomed`,
 *     `activeAccordion`,
 *   • dropped `onAccordionChange` (Ionic-style, mai chiamato),
 *   • dropped doppia injection `openSrv` + `closeServ` (era
 *     `OpenModalService` injectato due volte come due alias),
 *   • switch a `inject()` + `takeUntilDestroyed`,
 *   • aggiunto safe-guard `parseFloat(?.toString())` con fallback '0'
 *     (era `parseFloat(result.pr_price.toString())` che esplode se
 *     `pr_price` è null/undefined).
 *
 * Behaviour preserved 1:1: getProduct → patch → triggerRefresh dopo
 * delete, Swal success feedback, deleteProductPosition flow.
 */
@Component({
  selector: 'app-product-detail-modal',
  templateUrl: './product-detail-modal.component.html',
  styleUrls: ['./product-detail-modal.component.scss'],
  standalone: false,
})


export class ProductDetailModalComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly productSrv = inject(ProductsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly openSrv = inject(OpenModalService);
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs ────────────────────────────────────────────────────────
  @Input() opened = false;
  @Input() storicoOn = false;
  @Input() pr_code = '';

  // ── Data ──────────────────────────────────────────────────────────
  public product: Products.ProductDetails = new Products.ProductDetails(null);
  public pr_position: Products.IProductPosition[] = [];

  // ── UI state ──────────────────────────────────────────────────────
  public loading = false;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.triggerRefresh();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader.
  // ──────────────────────────────────────────────────────────────────

  triggerRefresh(): void {
    if (!this.pr_code) return;
    this.loading = true;

    this.productSrv
      .getProduct(this.pr_code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: Products.IProductDetails) => {
          if (!result) {
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          // Safe-guard against null/undefined numeric fields.
          this.product = {
            ...result,
            pr_price: parseFloat(result.pr_price?.toString().replace(',', '.') ?? '0'),
            pr_weight: parseFloat(result.pr_weight?.toString().replace(',', '.') ?? '0'),
            pr_stock: parseFloat(result.pr_stock?.toString().replace(',', '.') ?? '0'),
            committed: parseFloat(result.committed?.toString().replace(',', '.') ?? '0'),
            available: parseFloat(result.available?.toString().replace(',', '.') ?? '0'),
          };
          this.pr_position = this.product.pr_positions ?? [];
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Position actions.
  // ──────────────────────────────────────────────────────────────────

  /** Opens the add-position sub-modal (`product-position`). */
  openAddPosition(): void {
    this.openSrv.onPos();
  }

  /** Confirms + deletes a warehouse position. */
  deleteProductPosition(pr_id: string | number): void {
    if (!pr_id) return;

    Swal.fire({
      title: 'Eliminare la posizione?',
      text: 'La posizione magazzino verrà rimossa permanentemente.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sì, elimina',
      cancelButtonText: 'Annulla',
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.productSrv
        .deleteProduct(String(pr_id))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: any) => {
            if (data) {
              Swal.fire({
                icon: 'success',
                title: 'Eliminata',
                text: 'Posizione eliminata con successo.',
                timer: 1500,
                timerProgressBar: true,
              });
              this.triggerRefresh();
            } 
            else {
              Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Posizione non eliminata, riprova.',
                timer: 1500,
                timerProgressBar: true,
              });
            }
          },
          error: () => {
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text: 'Errore di rete, riprova.',
              timer: 1500,
              timerProgressBar: true,
            });
          },
        });
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // UI helpers.
  // ──────────────────────────────────────────────────────────────────

  isValidNumber(val: unknown): boolean {
    return val !== null && val !== undefined && val !== '' && !isNaN(+val!);
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.closeServ.prodDetOff();
    this.closeServ.offStoricoProd();
  }
  
}
